import { ref, watch, computed } from 'vue';
import type { CurrencyPricingDataFragmentFragment, GetPlanPricingQuery, PricingDataFragmentFragment } from '@/gql/graphql';
import { showErrorPopupDialog } from '@/use/swalDialog';

export type PlanPricing = GetPlanPricingQuery['getPlanPricing'][number];
export type CallerPlanPrice = Extract<PlanPricing['monthly'], {}>;
export type CallerPlanPricingData = CurrencyPricingDataFragmentFragment;
export type CallerPlanPricingCurrency = keyof CallerPlanPricingData;
export type AmountData = PricingDataFragmentFragment;

export type BenefitItemView = {
  title: string;
  tooltipText?: string;
  isParent?: boolean;
  field?: Exclude<keyof CallerPlanPrice, 'price' | 'planKind'>;
  isNewFeature?: boolean;
  details?: BenefitItemView[];
  default?: string;
};

export const planPricing = ref<PlanPricing[]>([]);
export const planCurrency = ref<CallerPlanPricingCurrency>('USD');
export const billingPeriod = ref<'monthly' | 'annual'>('annual');

export const isAnnualPlanning = computed(() => {
  return billingPeriod.value === 'annual';
});
export const isMonthlyPlanning = computed(() => {
  return billingPeriod.value === 'monthly';
});

export const visiblePlans = computed(() => {
  return planPricing.value?.map((pricing) => ({ plan: pricing[billingPeriod.value], pricing })).filter((p) => p.plan) as {
    plan: CallerPlanPrice;
    pricing: PlanPricing;
  }[];
});

export const getShortKind = (kind: string) => {
  return kind.replace('Annual', '');
};

// Format a single price amount with appropriate decimal places
const formatAmount = (amount: number): string => {
  if (amount === 0) return '$0';

  if (amount % 1) {
    return `$${amount.toFixed(2)}`;
  } else {
    return `$${amount.toFixed(0)}`;
  }
};

// Format tier pricing data to display string (e.g., "$7 - $13/order" or "$8/order")
export const formatTierData = (data: AmountData): string => {
  const { description, minAmount, maxAmount, customAmount } = data;

  if (maxAmount != null && minAmount != null) {
    return `${formatAmount(minAmount)} - ${formatAmount(maxAmount)}${description || ''}`;
  }

  if (minAmount) {
    return `${formatAmount(minAmount)}${description || ''}`;
  }

  if (customAmount) {
    return customAmount;
  }

  return 'Included';
};

watch(planPricing, (val) => {
  if (val.length === 0) {
    showErrorPopupDialog({
      title: 'Plan Pricing error',
      text: 'Failed to fetch plan pricing, make sure your internet is working and reload customer info. If this error persists then please notify @eng',
    });
  }
});
